import { useEffect, useState, useRef } from "react";
import { api, currency } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, FileText, Pencil, GitBranch } from "lucide-react";

const CHANNELS = ["Paid Search", "TV", "Billboard", "Directory", "Lead Purchase", "Radio", "Social", "Referral", "Organic"];
const STAGES = ["inquiry", "qualified", "consultation", "signed", "lost"];
const STAGE_STYLE = {
  inquiry: "bg-[#5E6B78]/10 text-[#5E6B78]",
  qualified: "bg-[#0E7C7B]/10 text-[#0E7C7B]",
  consultation: "bg-[#174A7E]/10 text-[#174A7E]",
  signed: "bg-[#156F52]/10 text-[#156F52]",
  lost: "bg-[#B54747]/10 text-[#B54747]",
};

const empty = {
  name: "",
  channel: "Paid Search",
  campaign: "",
  vendor_id: "none",
  practice_area: "",
  office: "",
  stage: "inquiry",
  sources: [], // additional touch channels (besides primary channel)
  source_weights: {},
  estimated_revenue: 0,
  collected_revenue: 0,
  contacted_within_15min: false,
  response_time_minutes: 0,
  rejection_reason: "",
  source_recorded: true,
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("all");
  const [attribution, setAttribution] = useState(null);
  const [attrMode, setAttrMode] = useState("multi"); // multi | single
  const [importReport, setImportReport] = useState(null);
  const csvRef = useRef(null);
  const { user } = useAuth();
  const canImport = can(user?.role, "lead:import");
  const canWrite = can(user?.role, "lead:write");

  const load = () => {
    api.get("/leads").then((r) => setLeads(r.data));
    api.get("/vendors").then((r) => setVendors(r.data));
    api.get("/attribution").then((r) => setAttribution(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => {
    setEditingId(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (l) => {
    setEditingId(l.id);
    setForm({
      name: l.name || "",
      channel: l.channel || "Paid Search",
      campaign: l.campaign || "",
      vendor_id: l.vendor_id || "none",
      practice_area: l.practice_area || "",
      office: l.office || "",
      stage: l.stage || "inquiry",
      sources: (l.sources || []).filter((s) => s !== (l.channel || "")),
      source_weights: l.source_weights || {},
      estimated_revenue: l.estimated_revenue || 0,
      collected_revenue: l.collected_revenue || 0,
      contacted_within_15min: !!l.contacted_within_15min,
      response_time_minutes: l.response_time_minutes || 0,
      rejection_reason: l.rejection_reason || "",
      source_recorded: l.source_recorded !== false,
    });
    setOpen(true);
  };

  const toggleSource = (ch) => {
    setForm((f) => ({
      ...f,
      sources: f.sources.includes(ch) ? f.sources.filter((s) => s !== ch) : [...f.sources, ch],
    }));
  };

  const setWeight = (ch, val) => {
    setForm((f) => ({ ...f, source_weights: { ...f.source_weights, [ch]: val } }));
  };

  const save = async () => {
    if (!form.name || !form.practice_area || !form.office)
      return toast.error("Name, practice area and office are required");
    const allSources = Array.from(new Set([form.channel, ...form.sources]));
    // Only send custom weights if the user entered at least one non-empty value
    const entered = allSources.filter((s) => form.source_weights[s] !== "" && form.source_weights[s] != null);
    const weights = entered.length
      ? allSources.reduce((acc, s) => ({ ...acc, [s]: Number(form.source_weights[s]) || 0 }), {})
      : null;
    const payload = {
      ...form,
      vendor_id: form.vendor_id === "none" ? null : form.vendor_id,
      sources: allSources,
      source_weights: weights,
      estimated_revenue: Number(form.estimated_revenue) || 0,
      collected_revenue: Number(form.collected_revenue) || 0,
      response_time_minutes: Number(form.response_time_minutes) || 0,
    };
    try {
      if (editingId) {
        await api.put(`/leads/${editingId}`, payload);
        toast.success("Lead updated");
      } else {
        await api.post("/leads", payload);
        toast.success("Lead added");
      }
      setOpen(false);
      setForm(empty);
      setEditingId(null);
      load();
    } catch {
      toast.error("Could not save the lead");
    }
  };

  const remove = async (id) => {
    await api.delete(`/leads/${id}`);
    load();
  };

  const importCsv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/leads/import", fd);
      toast.success(`Imported ${data.imported} leads${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      if (data.skipped > 0) setImportReport(data);
      load();
    } catch {
      toast.error("Import failed — check CSV columns");
    }
    e.target.value = "";
  };

  const vName = (id) => vendors.find((v) => v.id === id)?.name || "—";
  const shown = filter === "all" ? leads : leads.filter((l) => l.stage === filter);
  const attrRows = attribution ? (attrMode === "multi" ? attribution.multi_touch : attribution.single_touch) : [];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#B54747]">Attribution</p>
          <h1 className="font-display text-4xl font-light mt-1">Lead-to-Case</h1>
        </div>
        <div className="flex gap-2">
          {canImport && (
            <>
              <input ref={csvRef} type="file" accept=".csv" className="hidden" onChange={importCsv} data-testid="leads-csv-input" />
              <Button variant="outline" className="rounded-none" onClick={() => csvRef.current?.click()} data-testid="import-leads-csv-button">
                <FileText className="h-4 w-4 mr-1" /> Import CSV
              </Button>
            </>
          )}
          {canWrite && (
            <Button className="bg-[#0B1F33] text-white rounded-none hover:bg-zinc-800" onClick={openNew} data-testid="add-lead-button">
              <Plus className="h-4 w-4 mr-1" /> Add lead
            </Button>
          )}
        </div>
      </div>

      {/* Multi-touch attribution panel */}
      {attribution && attrRows.length > 0 && (
        <div className="border border-zinc-200 bg-white mb-8" data-testid="attribution-panel">
          <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
            <div className="flex items-center gap-2">
              <GitBranch className="h-4 w-4 text-[#174A7E]" />
              <h2 className="font-display text-xl">Channel attribution</h2>
            </div>
            <div className="flex border border-zinc-300">
              <button
                onClick={() => setAttrMode("multi")}
                data-testid="attr-mode-multi"
                className={`px-3 py-1.5 text-xs font-mono-data uppercase tracking-wider transition-colors ${attrMode === "multi" ? "bg-[#0B1F33] text-white" : "text-zinc-600 hover:bg-zinc-100"}`}
              >
                Multi-touch
              </button>
              <button
                onClick={() => setAttrMode("single")}
                data-testid="attr-mode-single"
                className={`px-3 py-1.5 text-xs font-mono-data uppercase tracking-wider transition-colors ${attrMode === "single" ? "bg-[#0B1F33] text-white" : "text-zinc-600 hover:bg-zinc-100"}`}
              >
                Single-touch
              </button>
            </div>
          </div>
          <p className="px-5 pt-3 text-xs text-zinc-500">
            {attrMode === "multi"
              ? "Every channel that touched a signed case shares the credit equally (linear multi-touch)."
              : "All credit assigned to each case's primary channel only."}
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Channel</TableHead>
                <TableHead className="text-right">Leads touched</TableHead>
                <TableHead className="text-right">Signed (credited)</TableHead>
                <TableHead className="text-right">Revenue (credited)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attrRows.map((c) => (
                <TableRow key={c.channel} data-testid={`attr-row-${c.channel}`}>
                  <TableCell className="font-medium">{c.channel}</TableCell>
                  <TableCell className="text-right font-mono-data">{c.leads}</TableCell>
                  <TableCell className="text-right font-mono-data">{Number(c.signed).toFixed(attrMode === "multi" ? 2 : 0)}</TableCell>
                  <TableCell className="text-right font-mono-data">{currency(c.revenue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="flex gap-2 mb-4 flex-wrap">
        {["all", ...STAGES].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`lead-filter-${s}`}
            className={`px-3 py-1.5 text-xs font-mono-data uppercase tracking-wider border transition-colors ${
              filter === s ? "bg-[#0B1F33] text-white border-[#0B1F33]" : "border-zinc-300 text-zinc-600 hover:bg-zinc-100"
            }`}
          >
            {s} {s !== "all" && `(${leads.filter((l) => l.stage === s).length})`}
          </button>
        ))}
      </div>

      <div className="border border-zinc-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Lead</TableHead>
              <TableHead>Sources</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead>Practice</TableHead>
              <TableHead>Stage</TableHead>
              <TableHead>15-min</TableHead>
              <TableHead className="text-right">Fee</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((l) => {
              const srcs = (l.sources && l.sources.length ? l.sources : [l.channel]).filter(Boolean);
              return (
                <TableRow key={l.id} className="hover:bg-zinc-50" data-testid={`lead-row-${l.id}`}>
                  <TableCell className="font-medium">{l.name}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {srcs.map((s, i) => (
                        <span
                          key={s + i}
                          className={`px-1.5 py-0.5 text-[10px] font-mono-data ${i === 0 ? "bg-[#174A7E]/10 text-[#174A7E]" : "bg-zinc-100 text-zinc-500"}`}
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-zinc-500 text-xs">{vName(l.vendor_id)}</TableCell>
                  <TableCell className="text-xs">{l.practice_area}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 text-xs font-mono-data uppercase ${STAGE_STYLE[l.stage]}`}>{l.stage}</span>
                  </TableCell>
                  <TableCell>
                    {["qualified", "consultation", "signed"].includes(l.stage) ? (
                      l.contacted_within_15min ? (
                        <span className="text-[#156F52] text-xs">✓</span>
                      ) : (
                        <span className="text-[#B54747] text-xs font-medium">missed</span>
                      )
                    ) : (
                      <span className="text-zinc-300">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono-data">
                    {l.stage === "signed" ? currency(l.collected_revenue || l.estimated_revenue) : "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-2">
                      {canWrite && (
                        <button onClick={() => openEdit(l)} className="text-zinc-300 hover:text-[#174A7E]" data-testid={`edit-lead-${l.id}`}>
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      {canWrite && (
                        <button onClick={() => remove(l.id)} className="text-zinc-300 hover:text-[#B54747]" data-testid={`delete-lead-${l.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {shown.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-zinc-500 py-10">No leads.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editingId ? "Edit lead" : "New lead"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <Label>Lead name / reference</Label>
              <Input value={form.name} onChange={upd("name")} data-testid="lead-name-input" className="mt-1 rounded-none" />
            </div>
            <div>
              <Label>Primary channel</Label>
              <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v, sources: form.sources.filter((s) => s !== v) })}>
                <SelectTrigger className="mt-1 rounded-none" data-testid="lead-channel-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CHANNELS.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Stage</Label>
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger className="mt-1 rounded-none" data-testid="lead-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => (<SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Additional touch sources</Label>
              <p className="text-xs text-zinc-500 mt-0.5">Every source that influenced this case shares the credit.</p>
              <div className="flex flex-wrap gap-2 mt-2" data-testid="lead-sources-multiselect">
                {CHANNELS.filter((c) => c !== form.channel).map((c) => {
                  const active = form.sources.includes(c);
                  return (
                    <button
                      type="button"
                      key={c}
                      onClick={() => toggleSource(c)}
                      data-testid={`lead-source-${c}`}
                      className={`px-2.5 py-1 text-xs font-mono-data border transition-colors ${active ? "bg-[#174A7E] text-white border-[#174A7E]" : "border-zinc-300 text-zinc-600 hover:bg-zinc-100"}`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
            {(() => {
              const all = Array.from(new Set([form.channel, ...form.sources]));
              if (all.length < 2) return null;
              return (
                <div className="col-span-2 border-t border-zinc-100 pt-3" data-testid="lead-weights-section">
                  <Label>Custom source weights (optional)</Label>
                  <p className="text-xs text-zinc-500 mt-0.5">Leave blank to split credit evenly. Enter relative numbers (e.g. 60 / 40) to weight touches.</p>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {all.map((s) => (
                      <div key={s} className="flex items-center gap-2">
                        <span className="text-xs font-mono-data text-zinc-600 flex-1 truncate">{s}</span>
                        <Input
                          type="number"
                          min="0"
                          value={form.source_weights[s] ?? ""}
                          onChange={(e) => setWeight(s, e.target.value)}
                          data-testid={`lead-weight-${s}`}
                          placeholder="auto"
                          className="w-20 h-8 rounded-none text-xs"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            <div>
              <Label>Practice area</Label>
              <Input value={form.practice_area} onChange={upd("practice_area")} className="mt-1 rounded-none" />
            </div>
            <div>
              <Label>Office</Label>
              <Input value={form.office} onChange={upd("office")} className="mt-1 rounded-none" />
            </div>
            <div className="col-span-2">
              <Label>Vendor</Label>
              <Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                <SelectTrigger className="mt-1 rounded-none" data-testid="lead-vendor-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {vendors.map((v) => (<SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Estimated fee ($)</Label>
              <Input type="number" value={form.estimated_revenue} onChange={upd("estimated_revenue")} className="mt-1 rounded-none" />
            </div>
            <div>
              <Label>Collected fee ($)</Label>
              <Input type="number" value={form.collected_revenue} onChange={upd("collected_revenue")} className="mt-1 rounded-none" />
            </div>
            <div className="col-span-2 flex items-center gap-2 mt-2">
              <Checkbox
                id="contacted"
                checked={form.contacted_within_15min}
                onCheckedChange={(v) => setForm({ ...form, contacted_within_15min: !!v })}
                data-testid="lead-contacted-checkbox"
              />
              <Label htmlFor="contacted">Contacted within 15 minutes</Label>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} className="bg-[#0B1F33] text-white rounded-none" data-testid="save-lead-button">
              {editingId ? "Save changes" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV skipped-row report */}
      <Dialog open={!!importReport} onOpenChange={(o) => !o && setImportReport(null)}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="import-report-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Import summary</DialogTitle>
          </DialogHeader>
          {importReport && (
            <div className="space-y-4">
              <div className="flex gap-6">
                <div>
                  <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">Imported</p>
                  <p className="font-display text-3xl text-[#156F52]">{importReport.imported}</p>
                </div>
                <div>
                  <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">Skipped</p>
                  <p className="font-display text-3xl text-[#B54747]">{importReport.skipped}</p>
                </div>
              </div>
              {importReport.skipped_rows?.length > 0 && (
                <div className="border border-zinc-200">
                  <div className="px-3 py-2 bg-zinc-50 font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">Skipped rows</div>
                  <ul className="divide-y divide-zinc-100 text-sm max-h-64 overflow-y-auto">
                    {importReport.skipped_rows.map((s, i) => (
                      <li key={i} className="px-3 py-2 flex justify-between gap-3" data-testid={`skipped-row-${i}`}>
                        <span className="text-zinc-500">Row {s.row}</span>
                        <span className="text-zinc-700">{s.reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setImportReport(null)} className="bg-[#0B1F33] text-white rounded-none" data-testid="close-import-report">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
